// src/service/api/data.ts

import { API_BASE_URL } from "@/utils/getApiBase";
import { request } from "../request";

/** get menu list */
export function fetchApiList() {
  return request<Api.Data.ApiListItem[]>({
    url: `${API_BASE_URL}/api/platform/api-list/`,
    method: "get",
  });
}

export function fetchApiKeyById(apiKeyId: string) {
  return request<Api.Data.ApiKey>({
    url: `${API_BASE_URL}/api/platform/api-key/`,
    method: "get",
    params: {
      id: apiKeyId,
    },
    metadata: {
      operationType: "read",
      isUserInitiated: true,
      operationName: "fetchApiKeyById",
      showUserErrors: true,
    },
  });
}

export function fetchAvailableModelList() {
  return request<Api.Data.ModelListResponse>({
    url: "https://api.avalai.ir/public/models/",
    method: "get",
  });
}

export function createApiKey(
  name: string,
  permissions?: Api.Data.ApiKeyPermissions
) {
  return request<Api.Data.ApiKey>({
    url: `${API_BASE_URL}/api/platform/create-key/`,
    method: "post",
    data: {
      name,
      permissions,
    },
    metadata: {
      operationType: "write",
      isUserInitiated: true,
      operationName: "createApiKey",
      showUserErrors: true,
    },
  });
}

export function editApiKey(params: Api.Data.EditApiKeyParams) {
  return request<Api.Data.ApiEditItem>({
    url: `${API_BASE_URL}/api/platform/edit-key/`,
    method: "post",
    data: params,
    metadata: {
      operationType: "write",
      isUserInitiated: true,
      operationName: "editApiKey",
      showUserErrors: true,
    },
  });
}

export function removeApiKey(id: string) {
  return request<any>({
    url: `${API_BASE_URL}/api/platform/remove-key/`,
    method: "post",
    data: {
      id,
    },
  });
}

export function fetchRateLimitsData() {
  return request<Api.Data.ModelRateLimits>({
    url: `${API_BASE_URL}/api/data/platform/rate-limits/`,
    method: "get",
  });
}

export function fetchAllRateLimitsData() {
  return request<string | null>({
    url: `${API_BASE_URL}/api/data/platform/all-rate-limits/`,
    method: "get",
  });
}

/** Staff-promoted models for /usage/recommendations. Fails silently: callers fall back to the curated lineage. */
export function fetchPromotedModelsData() {
  return request<unknown>({
    url: `${API_BASE_URL}/api/data/platform/model-recommendations/`,
    method: "get",
    metadata: {
      operationType: "read",
      isUserInitiated: false,
      operationName: "fetchPromotedModels",
      showUserErrors: false,
    },
  });
}

export function sendUserFeedback(title: string, body: string) {
  return request<any>({
    url: `${API_BASE_URL}/api/feedback/`,
    method: "post",
    data: {
      title,
      body,
    },
  });
}

export function fetchInvitedUsers() {
  return request<any>({
    url: `${API_BASE_URL}/api/data/invited-users/`,
    method: "get",
  });
}

export function fetchExchangeRate() {
  return request<Api.Data.ExchangeRate>({
    url: `${API_BASE_URL}/api/data/exchange-rate/`,
    method: "get",
  });
}

// API Functions
export function uploadFile(
  file: File,
  purpose: string,
  metadata?: Record<string, any>
) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("purpose", purpose);
  if (metadata) {
    formData.append("metadata", JSON.stringify(metadata));
  }

  return request<Api.Storage.FileItem>({
    url: `${API_BASE_URL}/api/platform/files/`,
    method: "post",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export function listFiles(params?: {
  purpose?: string;
  limit?: number;
  order?: "desc" | "asc";
  after?: string;
}) {
  return request<Api.Storage.FileListResponse>({
    url: `${API_BASE_URL}/api/platform/files/`,
    method: "get",
    params,
  });
}

export function getFile(fileId: string) {
  return request<Api.Storage.FileItem>({
    url: `${API_BASE_URL}/api/platform/files/${fileId}/`,
    method: "get",
  });
}

export function deleteFile(fileId: string) {
  return request<Api.Storage.FileDeleteResponse>({
    url: `${API_BASE_URL}/api/platform/files/${fileId}/`,
    method: "delete",
  });
}

export function getFileContent(fileId: string) {
  return request<any>({
    url: `${API_BASE_URL}/api/platform/files/${fileId}/content/`,
    method: "get",
  });
}

export function fetchDashboardKey() {
  return request<Api.Data.DashboardKey>({
    url: `${API_BASE_URL}/api/platform/dashboard-key/`,
    method: "get",
    metadata: {
      operationType: "write",
      isUserInitiated: true,
      operationName: "fetchDashboardKey",
      showUserErrors: true,
    },
  });
}

/** Rotate the dashboard key now; the previous secret stops working immediately. */
export function rotateDashboardKey() {
  return request<Api.Data.DashboardKey>({
    url: `${API_BASE_URL}/api/platform/dashboard-key/`,
    method: "post",
    metadata: {
      operationType: "write",
      isUserInitiated: true,
      operationName: "rotateDashboardKey",
      showUserErrors: true,
    },
  });
}

export function createVectorStore(data: Api.Storage.CreateVectorStoreParams) {
  return request<Api.Storage.VectorStoreItem>({
    url: `${API_BASE_URL}/api/platform/vector-stores/`,
    method: "post",
    data,
  });
}
export function listVectorStores(params?: {
  limit?: number;
  order?: "desc" | "asc";
  after?: string;
}) {
  return request<Api.Storage.VectorStoreListResponse>({
    url: `${API_BASE_URL}/api/platform/vector-stores/`,
    method: "get",
    params,
  });
}

export function getVectorStore(vectorStoreId: string) {
  return request<Api.Storage.VectorStoreItem>({
    url: `${API_BASE_URL}/api/platform/vector-stores/${vectorStoreId}/`,
    method: "get",
  });
}

export function deleteVectorStore(vectorStoreId: string) {
  return request<Api.Storage.VectorStoreDeleteResponse>({
    url: `${API_BASE_URL}/api/platform/vector-stores/${vectorStoreId}/`,
    method: "delete",
  });
}

export function updateVectorStore(
  vectorStoreId: string,
  data: Partial<Api.Storage.VectorStoreItem>
) {
  return request<Api.Storage.VectorStoreItem>({
    url: `${API_BASE_URL}/api/platform/vector-stores/${vectorStoreId}/`,
    method: "patch",
    data,
  });
}

export function attachFileToVectorStore(vectorStoreId: string, fileId: string) {
  return request<Api.Storage.VectorStoreFile>({
    url: `${API_BASE_URL}/api/platform/vector-stores/${vectorStoreId}/files/`,
    method: "post",
    data: { file_id: fileId },
  });
}

// Fetch guardrail settings
export function fetchGuardrailSettings(scope: string, apiKeyId?: string) {
 return request<Api.Data.GuardrailSettings>({
   url: `${API_BASE_URL}/api/platform/guardrail-settings/`,
   method: "get",
   params: {
     scope,
     ...(apiKeyId && { api_key_id: apiKeyId })
   }
 });
}

// Update guardrail settings
export function updateGuardrailSettings(
 scope: string,
 enabled: boolean,
 apiKeyId?: string,
 guardrailTypes?: string[],
 inheritFromParent?: boolean
) {
 return request<Api.Data.GuardrailSettings>({
   url: `${API_BASE_URL}/api/platform/guardrail-settings/`,
   method: "post",
   data: {
     scope,
     enabled,
     ...(apiKeyId && { api_key_id: apiKeyId }),
     ...(guardrailTypes && { guardrail_types: guardrailTypes }),
     ...(inheritFromParent !== undefined && { inherit_from_parent: inheritFromParent })
   }
 });
}
